<template>
    <section
        class="appointment-amount"
        aria-label="订单金额明细"
        aria-live="polite"
    >
        <p v-if="loading" role="status">正在获取金额明细…</p>
        <div v-else-if="error" role="alert">
            <p>{{ error }}</p>
            <button type="button" @click="load">重新加载金额</button>
        </div>
        <template v-else-if="projection">
            <dl>
                <div>
                    <dt>原价</dt>
                    <dd>¥{{ projection.preCouponAmount }}</dd>
                </div>
                <div v-if="projection.selectedCoupon">
                    <dt>
                        优惠券 · {{ couponBenefit(projection.selectedCoupon) }}
                    </dt>
                    <dd>
                        {{
                            projection.selectedCoupon.templateName ||
                            "已选优惠券"
                        }}
                    </dd>
                </div>
                <div class="discount">
                    <dt>总优惠</dt>
                    <dd>-¥{{ projection.discountAmount }}</dd>
                </div>
                <div>
                    <dt>应付金额</dt>
                    <dd>¥{{ projection.payableAmount }}</dd>
                </div>
                <div class="paid">
                    <dt>实付金额</dt>
                    <dd>¥{{ projection.paidAmount }}</dd>
                </div>
            </dl>
            <p v-if="!projection.selectedCoupon">未使用优惠券</p>
            <details v-if="projection.selectedCoupon">
                <summary>查看优惠券规则</summary>
                <CouponRules :coupon="projection.selectedCoupon" />
            </details>
            <details v-if="projection.allocations.length">
                <summary>查看订单项分摊</summary>
                <dl
                    v-for="(item, index) in projection.allocations"
                    :key="index"
                >
                    <div>
                        <dt>{{ itemLabel(item.businessItemType) }}</dt>
                        <dd>
                            原价 ¥{{ amount(item.preCouponAmount) }} · 优惠 ¥{{
                                amount(item.allocatedDiscountAmount)
                            }}
                            · 应付 ¥{{ amount(item.cashPayableAmount) }}
                        </dd>
                    </div>
                </dl>
            </details>
            <p class="notice">
                实付金额以本预约成功付款事实为准；退款进度请查看退款记录。
            </p>
        </template>
    </section>
</template>
<script>
import request from "@/tool/request";
import API from "@/tool/api";
import { unwrapResult, getResultMessage } from "@/tool/result";
import CouponRules from "@/components/coupons/CouponRules.vue";
import couponPresentation from "@/tool/couponPresentation.cjs";
export default {
    name: "AppointmentAmount",
    components: { CouponRules },
    props: {
        appointmentNo: { type: String, required: true },
        ownerKey: { default: "" },
        refreshKey: { default: 0 },
    },
    data() {
        return { projection: null, error: "", loading: false, generation: 0 };
    },
    mounted() {
        this.load();
    },
    beforeDestroy() {
        this.invalidate();
    },
    watch: {
        refreshKey() {
            this.invalidate();
            this.load();
        },
        appointmentNo() {
            this.invalidate();
            this.load();
        },
        ownerKey() {
            this.invalidate();
        },
    },
    methods: {
        couponBenefit: couponPresentation.couponBenefit,
        amount(value) {
            return value !== null &&
                value !== undefined &&
                value !== "" &&
                Number.isFinite(Number(value))
                ? Number(value).toFixed(2)
                : "未提供";
        },
        itemLabel(type) {
            return (
                {
                    PHOTOGRAPHY: "摄影服务",
                    PHOTOGRAPHY_SERVICE: "摄影服务",
                    MAKEUP: "化妆服务",
                    MAKEUP_SERVICE: "化妆服务",
                    VENUE: "场地",
                    PLATFORM: "平台费",
                }[type] ||
                type ||
                "订单项"
            );
        },
        invalidate() {
            this.generation += 1;
            this.projection = null;
            this.error = "";
            this.loading = false;
        },
        async load() {
            const generation = ++this.generation;
            const appointmentNo = this.appointmentNo;
            this.loading = true;
            this.error = "";
            this.projection = null;
            try {
                const value = unwrapResult(
                    await request.get(
                        API.photography.appointmentCoupons(appointmentNo),
                        { headers: { "Cache-Control": "no-store" } }
                    ),
                    "金额明细读取失败"
                );
                if (generation !== this.generation) return;
                if (
                    !value ||
                    value.appointmentNo !== appointmentNo ||
                    [
                        "preCouponAmount",
                        "discountAmount",
                        "payableAmount",
                        "paidAmount",
                    ].some((key) => this.amount(value[key]) === "未提供")
                )
                    throw new Error("金额信息不完整，请重新加载");
                this.projection = {
                    ...value,
                    allocations: Array.isArray(value.allocations)
                        ? value.allocations
                        : [],
                };
            } catch (error) {
                if (generation === this.generation)
                    this.error = getResultMessage(error, "金额明细读取失败");
            } finally {
                if (generation === this.generation) this.loading = false;
            }
        },
    },
};
</script>
<style scoped>
.appointment-amount {
    font-size: 13px;
    line-height: 1.6;
    min-width: 0;
}
dl {
    margin: 0;
}
dl > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 7px 0;
}
dt {
    color: #777780;
}
dd {
    margin: 0;
    text-align: right;
    overflow-wrap: anywhere;
}
.discount,
.discount dt {
    color: #d84b53;
}
.paid {
    border-top: 1px solid #e9e9ef;
    font-weight: 700;
}
summary,
button {
    cursor: pointer;
    color: #6b5dd5;
}
summary {
    padding: 8px 0;
}
.notice {
    color: #777780;
    font-size: 12px;
}
button {
    border: 1px solid #e9e9ef;
    border-radius: 100px;
    background: #fff;
    padding: 9px 14px;
}
button:focus-visible,
summary:focus-visible {
    outline: 2px solid #6b5dd5;
    outline-offset: 3px;
}
</style>
